import * as ToastPrimitive from '@radix-ui/react-toast';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import type { IconName } from './icon-paths.js';
import { Icon } from './icons.js';

const TOAST_ICON: Record<ToastTone | 'success', IconName> = {
  danger: 'alert',
  info: 'info',
  success: 'check',
  warning: 'alert',
};

/**
 * Toasts (FTD §2.5, §12.3). There is **no optimistic success**: a success toast can only be raised
 * with the server's confirmation of the command (`status` and `requestId` from the `ApiResult`), so a
 * success message cannot be shown before the server answered. Pending work is shown in place (button
 * `loading`), never as a toast.
 */

export type ToastTone = 'info' | 'warning' | 'danger';

/** Proof that the server confirmed the command: the `ApiResult` of the 2xx response. */
export interface ServerConfirmation {
  readonly requestId: string | null;
  readonly status: number;
}

interface ToastItem {
  readonly description?: string | undefined;
  readonly id: number;
  readonly title: string;
  readonly tone: ToastTone | 'success';
}

export interface ToastApi {
  /** Informational, warning, or error notices (for example "retry later"). */
  notify(toast: {
    readonly description?: string;
    readonly title: string;
    readonly tone?: ToastTone;
  }): void;
  /** A success notice, only after the server confirmed (2xx). Throws for a non-2xx status. */
  success(toast: {
    readonly confirmedBy: ServerConfirmation;
    readonly description?: string;
    readonly title: string;
  }): void;
}

const ToastContext = createContext<ToastApi | null>(null);

let nextId = 1;

export function ToastProvider({ children }: { readonly children: ReactNode }) {
  const [items, setItems] = useState<readonly ToastItem[]>([]);

  const push = useCallback((item: Omit<ToastItem, 'id'>) => {
    const id = nextId++;
    setItems((current) => [...current, { ...item, id }]);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      notify: ({ description, title, tone = 'info' }) => push({ description, title, tone }),
      success: ({ confirmedBy, description, title }) => {
        if (confirmedBy.status < 200 || confirmedBy.status >= 300) {
          throw new Error('A success toast needs a 2xx server confirmation.');
        }
        push({ description, title, tone: 'success' });
      },
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      <ToastPrimitive.Provider duration={6000} label="اعلان‌ها" swipeDirection="left">
        {children}
        {items.map((item) => (
          <ToastPrimitive.Root
            className="saay-toast"
            data-tone={item.tone}
            key={item.id}
            onOpenChange={(open) => {
              if (!open) {
                setItems((current) => current.filter((candidate) => candidate.id !== item.id));
              }
            }}
            type={item.tone === 'danger' ? 'foreground' : 'background'}
          >
            <Icon name={TOAST_ICON[item.tone]} />
            <ToastPrimitive.Title className="saay-toast__title">{item.title}</ToastPrimitive.Title>
            {item.description === undefined ? null : (
              <ToastPrimitive.Description className="saay-toast__description">
                {item.description}
              </ToastPrimitive.Description>
            )}
            <ToastPrimitive.Close aria-label="بستن" className="saay-icon-button saay-toast__close">
              <Icon name="close" />
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        ))}
        <ToastPrimitive.Viewport className="saay-toast-viewport" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

export function useOptionalToast(): ToastApi | null {
  return useContext(ToastContext);
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (api === null) {
    throw new Error('useToast must be used inside <UiProvider>.');
  }
  return api;
}
